<template>
  <div class="war-room-share-popup">
    <div class="war-room-share-popup__header">
      Share this {{ sharableType }}
      <CloseIcon class="war-room-share-popup__close" @click="$emit('on-close')" />
    </div>
    <div class="war-room-share-popup__buttons">
      <button
        v-for="{ action, name, icon, url } in buttons"
        :key="name"
        class="war-room-share-popup__button"
        @click="share(url, name)"
      >
        <component :is="icon" class="war-room-share-popup__button-icon" />
        {{ action }}
      </button>
    </div>
    <div class="war-room-share-popup__referral">
      <WarRoomReferralLink :url="sharableUrl" @click.native="handleReferralLinkClick" />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
import CloseIcon from './images/close-icon.vue.svg';
import { sharingUrls, openShareWindow } from '@/components/utils/browser/socialSharing';
import WarRoomReferralLink from '@/components/partials/war-room/WarRoomReferralLink/WarRoomReferralLink.vue';

export default Vue.extend({
  name: 'WarRoomSharePopup',
  components: {
    CloseIcon,
    WarRoomReferralLink,
  },
  props: {
    sharableType: {
      type: String,
      default: '',
      required: true,
    } as PropOptions<string>,
    sharableUrl: {
      type: String,
      default: '',
      required: true,
    } as PropOptions<string>,
    sharableId: {
      type: Number,
      default: null,
      required: true,
    } as PropOptions<number>,
  },
  computed: {
    buttons() {
      return sharingUrls.map(socialNetwork => {
        const url = `${socialNetwork.url}${this.sharableUrl}`;

        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return { ...socialNetwork, icon: require(`./images/${socialNetwork.name}-logo.vue.svg`) as string, url };
      });
    },
  },
  methods: {
    share(url: string, name: string): void {
      this.$analytics.logEvent(`war-room__post__share-button__click`, {
        objectType: this.sharableType,
        socialNetwork: name,
        objectId: this.sharableId,
      });

      openShareWindow(url, name);
    },
    handleReferralLinkClick() {
      this.$analytics.logEvent(`war-room__post__share-popup-referral-link__click`, {
        objectType: this.sharableType,
        objectId: this.sharableId,
      });
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-share-popup {
  box-shadow: var(--theme-elevation-05);
  padding-bottom: 16px;
  background: var(--theme-color-card);
  border-radius: 8px;
  border: 1px solid var(--theme-color-background);

  &__header {
    @include subhead-semibold;

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 8px 8px 12px;
    color: var(--gray-50);
    margin-bottom: 12px;
  }

  &__close {
    cursor: pointer;
    color: var(--gray-50);
    transition: color 0.3s;

    &:hover {
      color: var(--gray-35);
    }
  }

  &__buttons {
    @include flex-center;

    padding: 0 13px;
    margin-bottom: 11px;
  }

  &__button {
    @include flex-center;
    @include border-hover(border);
    @include subhead-semibold;

    margin: 0 3px;
    padding: 8px 12px;
    border: 1px solid var(--theme-color-border);
    border-radius: 6px;
    color: var(--theme-color-text);
    cursor: pointer;
    outline: none;
    appearance: none;
    background: transparent;
  }

  &__button-icon {
    margin-right: 6px;
    color: inherit;
  }

  &__referral {
    @include flex-center;

    padding: 4px 40px;
  }
}
</style>
